import React, { useState, useEffect } from 'react';
import {
  CForm, CRow, CCol, CFormInput, CFormLabel, CFormTextarea,
  CButton, CSpinner, CAlert
} from '@coreui/react';
import CIcon from '@coreui/icons-react';
import { cilSave, cilX } from '@coreui/icons';
import PropTypes from 'prop-types';
import { useTranslation } from '../../hooks/useTranslation';

const ColorInputs = ({
  onSubmit, onCancel, isLoading = false,
  submitResponse = null, initialData = null, isEdit = false
}) => {
  const { t } = useTranslation();
  const [formData, setFormData] = useState({
    colorName: '',
    colorDescription: '',
    colorCode: ''
  });

  const [errors, setErrors] = useState({});
  const [showSuccess, setShowSuccess] = useState(false);

  useEffect(() => {
    if (initialData) {
      setFormData({
        colorName: initialData.ColorName || '',
        colorDescription: initialData.ColorDescription || '',
        colorCode: initialData.ColorCode || ''
      });
    }
  }, [initialData]);

  useEffect(() => {
    if (submitResponse?.Result?.ErrorCode === 0) {
      setShowSuccess(true);
      setTimeout(() => setShowSuccess(false), 2000);
      if (!isEdit) {
        setFormData({ colorName: '', colorDescription: '', colorCode: '' });
      }
    }
  }, [submitResponse, isEdit]);

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
    if (errors[name]) setErrors(prev => ({ ...prev, [name]: '' }));
  };

  const validateForm = () => {
    const newErrors = {};
    if (!formData.colorName.trim()) newErrors.colorName = t('colorNameRequired');
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    if (!validateForm()) return;

    const sessionUserId = localStorage.getItem('sessionUserId');
    if (!sessionUserId) {
      setErrors({ general: t('userSessionNotFound') });
      return;
    }

    const payload = {
      SessionUserId: sessionUserId,
      ColorName: formData.colorName.trim(),
      ColorDescription: formData.colorDescription.trim(),
      ColorCode: formData.colorCode.trim()
    };

    if (isEdit && initialData?.ColorId) {
      payload.ColorId = initialData.ColorId;
    }

    onSubmit(payload);
  };

  const handleCancel = () => {
    if (!isEdit) setFormData({ colorName: '', colorDescription: '', colorCode: '' });
    setErrors({});
    onCancel();
  };

  const getSuccessMessage = () => {
    if (submitResponse && submitResponse.Result && submitResponse.Result.ErrorCode === 0) {
      return isEdit ? t('colorUpdated') : t('colorCreated');
    }
    return '';
  };

  return (
    <div>
      {showSuccess && (
        <CAlert color="success" dismissible onClose={() => setShowSuccess(false)}>
          {getSuccessMessage()}
        </CAlert>
      )}

      {errors.general && (
        <CAlert color="danger" 
        dismissible onClose={() => setErrors(prev => ({ ...prev, general: '' }))}>
          {errors.general}
        </CAlert>
      )}

      {submitResponse && submitResponse.Result 
      && submitResponse.Result.ErrorCode !== 0 && submitResponse.Result.ErrorDescription && (
        <CAlert color="danger">
          {submitResponse.Result.ErrorDescription}
        </CAlert>
      )}

      <CForm onSubmit={handleSubmit}>
        <CRow className="mb-3">
          <CCol md={6}>
            <CFormLabel htmlFor="colorName">{t('colorName')}</CFormLabel>
            <CFormInput
              type="text"
              id="colorName"
              name="colorName"
              value={formData.colorName}
              onChange={handleInputChange}
              invalid={!!errors.colorName}
              disabled={isLoading}
              placeholder={t('enterColorName')}
            />
            {errors.colorName && <div className="invalid-feedback d-block">{errors.colorName}</div>}
          </CCol>
          <CCol md={6}>
            <CFormLabel htmlFor="colorCode">{t('colorCode')}</CFormLabel>
            <CFormInput
              type="text"
              id="colorCode"
              name="colorCode"
              value={formData.colorCode}
              onChange={handleInputChange}
              invalid={!!errors.colorCode}
              disabled={isLoading}
              placeholder={t('enterColorCode')}
            />
            {errors.colorCode && <div className="invalid-feedback d-block">{errors.colorCode}</div>}
          </CCol>
        </CRow>

        <CRow className="mb-3">
          <CCol md={12}>
            <CFormLabel htmlFor="colorDescription">{t('description')}</CFormLabel>
            <CFormTextarea
              id="colorDescription"
              name="colorDescription"
              rows={4}
              value={formData.colorDescription}
              onChange={handleInputChange}
              invalid={!!errors.colorDescription}
              disabled={isLoading}
              placeholder={t('enterColorDescription')}
            />
            {errors.colorDescription && (
              <div className="invalid-feedback d-block">{errors.colorDescription}</div>
            )}
          </CCol>
        </CRow>

        <CRow>
          <CCol className="d-flex gap-2">
            <CButton type="submit" color="primary" disabled={isLoading}>
              {isLoading
                ? (<><CSpinner size="sm" className="me-2" /> {t('saving')}</>)
                : (<><CIcon icon={cilSave} className="me-2" /> {t('save')}</>)}
            </CButton>
            <CButton color="secondary" variant="outline"
             onClick={handleCancel} disabled={isLoading}>
              <CIcon icon={cilX} className="me-2" /> {t('cancel')}
            </CButton>
          </CCol>
        </CRow>
      </CForm>
    </div>
  );
};

ColorInputs.propTypes = {
  onSubmit: PropTypes.func.isRequired,
  onCancel: PropTypes.func.isRequired,
  isLoading: PropTypes.bool,
  submitResponse: PropTypes.object,
  initialData: PropTypes.object,
  isEdit: PropTypes.bool,
};

export default ColorInputs;
